import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TID } from "../lib/testIds";

const STEPS = [
  {
    n: "01",
    t: "Discovery",
    d: "Workshops, audits, technical due diligence. We map your problem precisely before we touch code.",
  },
  {
    n: "02",
    t: "Architecture",
    d: "System design, threat modelling, AI evaluation harness, and a build plan with measurable milestones.",
  },
  {
    n: "03",
    t: "Execution",
    d: "Weekly shipping cadence. Type-safe, observable, secure by default — with you in the loop.",
  },
  {
    n: "04",
    t: "Operate",
    d: "Deployment, monitoring, on-call rotations and continuous improvement after launch.",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 30%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="process"
      data-testid={TID.process.section}
      ref={ref}
      className="relative py-28 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
              ◇ Process
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] text-primary-x">
              How we work.
            </h2>
          </div>
          <p className="md:max-w-sm text-secondary-x leading-relaxed">
            A deliberate, four-stage operating system that turns ambitious ideas
            into measurable, defensible software.
          </p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-y-24">
          {/* timeline rail */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/2 -translate-x-px w-px bg-hairline" />
          <motion.div
            style={{ height: lineHeight }}
            className="hidden md:block absolute top-0 left-1/2 -translate-x-px w-px"
          ><div className="h-full w-px" style={{ background: "linear-gradient(to bottom, var(--text-primary), var(--text-secondary), transparent)" }} /></motion.div>

          {STEPS.map((s, i) => {
            const left = i % 2 === 0;
            return (
              <motion.div
                key={s.n}
                data-testid={TID.process.step(s.n)}
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`md:col-span-12 grid grid-cols-1 md:grid-cols-12 md:items-center relative`}
              >
                <div className={`md:col-span-6 ${left ? "md:pr-16 md:text-right" : "md:order-2 md:pl-16"}`}>
                  <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-3">
                    Step {s.n}
                  </div>
                  <h3 className="font-display text-4xl md:text-6xl font-medium tracking-tight text-primary-x">
                    {s.t}
                  </h3>
                  <p className="mt-5 text-secondary-x leading-relaxed max-w-md md:inline-block">
                    {s.d}
                  </p>
                </div>
                <div className="hidden md:flex md:col-span-0 absolute left-1/2 -translate-x-1/2 items-center justify-center">
                  <span className="h-3 w-3 rounded-full" style={{ background: "var(--text-primary)", boxShadow: "0 0 24px var(--text-primary)" }} />
                </div>
                <div className={`md:col-span-6 ${left ? "md:order-2" : ""}`}>
                  <div className={`relative ${left ? "md:pl-16" : "md:pr-16"}`}>
                    <div className="font-display text-[14vw] md:text-[10rem] leading-none font-bold tracking-tighter" style={{ color: "var(--hairline)" }}>
                      {s.n}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
